import React from 'react';
import { getIn, useFormikContext } from 'formik';
import * as Yup from 'yup';

import Grid from '~/components/core/Atomic/Grid/Grid';
import Typography from '~/components/core/Atomic/Typography';
import { useCms } from '~/components/hooks/useCms';
import { useIncidentConfiguration } from '~/components/hooks/useIncidentConfiguration';
import useIsConfigurationFieldSupportedByCategories from '~/components/hooks/useIsConfigurationFieldSupportedByCategories';
import { useLob } from '~/components/hooks/useLob';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { isFeatureEnabled } from '~/Utils';

import CardDialog from '../../../CardDialog';

import {
  CustomSectionFragment,
  getCustomAdditionalInformationSections,
  getCustomSectionInitialValues,
  getCustomSectionPath,
  getCustomSectionValidationSchema,
  getHasRenderableFieldForSubtype,
  getIsCustomSectionActive,
  getIsCustomSectionMandatory,
} from './Fragments/CustomSectionFragment';
import {
  ACCIDENT_FOLLOW_UP_PATH,
  getInitialAccidentFollowUpInitialValidationSchema,
  getInitialAccidentFollowUpInitialValues,
  getInitialAccidentFollowUpSectionConfig,
  InitialAccidentFollowUpFragment,
} from './Fragments/InitialAccidentFollowUpFragment';
import {
  FAULT_ASSESSMENT_PATH,
  getInitialFaultAssessmentInitialValidationSchema,
  getInitialFaultAssessmentInitialValues,
  getInitialFaultAssessmentSectionConfig,
  InitialFaultAssessmentFragment,
} from './Fragments/InitialFaultAssessmentFragment';
import {
  getInitialTheftFollowUpInitialValidationSchema,
  getInitialTheftFollowUpInitialValues,
  getInitialTheftFollowUpSectionConfig,
  InitialTheftFollowUpFragment,
  THEFT_FOLLOW_UP_PATH,
} from './Fragments/InitialTheftFollowUpFragment';
import {
  getPoliceFireDepartmentInvolvementInitialValues,
  getPoliceFireDepartmentInvolvementSectionConfig,
  getPoliceFireDepartmentInvolvementValidationSchema,
  POLICE_AND_FIRE_DEPARTMENT_PATH,
  PoliceFireDepartmentInvolvementFragment,
} from './Fragments/PoliceFireDepartmentInvolvementFragment';
import {
  getIsSupplementaryInformationSectionActive,
  getSupplementaryInformationInitialValues,
  getSupplementaryInformationSectionConfig,
  getSupplementaryInformationSectionTitle,
  getSupplementaryInformationValidationSchema,
  SUPPLEMENTARY_INFORMATION_PATH,
  SupplementaryInformationFragment,
} from './Fragments/SupplementaryInformationFragment';
import AdditionalInformationSection from './AdditionalInformationSection';

import styles from './index.module.scss';

const DESCRIPTION = 'Please answer the questions listed in the following sections, if applicable.';

// Row ids of auto's built-in sections on the FNOL form. A row's id keys its `verified_<id>` /
// `checked_<id>` Formik fields, the same namespace a custom section's id keys, so every one of these
// is reserved against custom section ids (pinned by reservedRuntimeIds.test.ts).
export const AUTO_FNOL_SECTION_ROW_IDS = {
  policeAndFireDepartment: 'police_and_fire_department',
  initialFaultAssessment: 'initial_fault_assessment',
  initialTheftFollowUp: 'initial_theft_follow_up',
  initialAccidentFollowUp: 'initial_accident_follow_up',
};

// A custom section's row renders when the section is shown AND at least one field survives this claim's
// sub-type — without the second half a section whose every field is filtered out would render a row that
// opens a dialog with no fields. Deliberately the FIELD-level half only: a section excluded by its OWN
// category stays visible-but-disabled on the form (the product ruling pinned in 'a category-excluded
// section must not stop the claim'), so the section-level check getIsCustomSectionEnabled applies for the
// menu must not be used here. The panel, the verified_<id> schema and the rows all share this predicate.
const isCustomSectionRendered = (
  incidentConfiguration,
  section,
  isFieldSupportedBySubtype,
  isHierarchyFieldGroupsEnabled
) =>
  getIsCustomSectionActive(incidentConfiguration, section, isHierarchyFieldGroupsEnabled) &&
  getHasRenderableFieldForSubtype(
    incidentConfiguration,
    section,
    isFieldSupportedBySubtype,
    isHierarchyFieldGroupsEnabled
  );

const getSectionsValidationSchema = (
  lob,
  incidentConfiguration,
  isFieldSupportedBySubtype,
  isHierarchyFieldGroupsEnabled = false
) => {
  if (
    !getIsAdditionalInformationActive(
      incidentConfiguration,
      lob,
      isHierarchyFieldGroupsEnabled,
      isFieldSupportedBySubtype
    )
  ) {
    return {};
  }

  const validations = {
    verified_supplementary_information: getIsSupplementaryInformationSectionActive(
      incidentConfiguration,
      isHierarchyFieldGroupsEnabled,
      isFieldSupportedBySubtype
    )
      ? Yup.boolean().oneOf([true], 'Required')
      : undefined,
  };

  const autoBuiltInValidations =
    lob === 'auto_claim'
      ? {
          verified_initial_fault_assessment: getInitialFaultAssessmentSectionConfig(incidentConfiguration)?.active
            ? Yup.boolean().oneOf([true], 'Required')
            : undefined,
          verified_police_and_fire_department: getPoliceFireDepartmentInvolvementSectionConfig(incidentConfiguration)
            ?.active
            ? Yup.boolean().oneOf([true], 'Required')
            : undefined,
          verified_initial_theft_follow_up: getInitialTheftFollowUpSectionConfig(incidentConfiguration)?.active
            ? Yup.boolean().oneOf([true], 'Required')
            : undefined,
          verified_initial_accident_follow_up: getInitialAccidentFollowUpSectionConfig(incidentConfiguration)?.active
            ? Yup.boolean().oneOf([true], 'Required')
            : undefined,
        }
      : {};

  // Config-driven custom sections, every LOB. A section blocks submit (verified_<id>) only when its row
  // renders — the same active + renderable-for-subtype filter CUSTOM_SECTIONS_PROPS applies, since a hidden
  // row can never be verified — and it is effectively mandatory: either its own Required flag is set or
  // it holds a mandatory field (getIsCustomSectionMandatory mirrors the sibling promotion, so a mandatory
  // field can't be skipped by leaving the section flag off). Section-level Required is FE-only (the API
  // ignores it, mirroring Supplementary Information); per-field mandatory keeps its own API enforcement.
  const customSectionsValidations = {};
  getCustomAdditionalInformationSections(incidentConfiguration).forEach((section) => {
    if (
      isCustomSectionRendered(
        incidentConfiguration,
        section,
        isFieldSupportedBySubtype,
        isHierarchyFieldGroupsEnabled
      ) &&
      getIsCustomSectionMandatory(
        incidentConfiguration,
        section,
        isFieldSupportedBySubtype,
        isHierarchyFieldGroupsEnabled
      )
    ) {
      customSectionsValidations[`verified_${section.id}`] = Yup.boolean().oneOf([true], 'Required');
    }
  });

  return { ...validations, ...autoBuiltInValidations, ...customSectionsValidations };
};

// The panel is shown only when a section inside it renders a row, so it applies the same sub-type filter
// the rows do: a section whose only content the claim's sub-type leaves out does not show the panel.
const getIsAdditionalInformationActive = (
  incidentConfiguration,
  lob,
  isHierarchyFieldGroupsEnabled = false,
  isFieldSupportedBySubtype = () => true
) => {
  const isSupplementaryInformationSectionActive = getIsSupplementaryInformationSectionActive(
    incidentConfiguration,
    isHierarchyFieldGroupsEnabled,
    isFieldSupportedBySubtype
  );

  const hasRenderedCustomSection = getCustomAdditionalInformationSections(incidentConfiguration).some((section) =>
    isCustomSectionRendered(incidentConfiguration, section, isFieldSupportedBySubtype, isHierarchyFieldGroupsEnabled)
  );

  if (lob !== 'auto_claim') {
    return isSupplementaryInformationSectionActive || hasRenderedCustomSection;
  }

  const isAutoConfigsActive = [
    isSupplementaryInformationSectionActive,
    getPoliceFireDepartmentInvolvementSectionConfig(incidentConfiguration)?.active,
    getInitialFaultAssessmentSectionConfig(incidentConfiguration)?.active,
    getInitialTheftFollowUpSectionConfig(incidentConfiguration)?.active,
    getInitialAccidentFollowUpSectionConfig(incidentConfiguration)?.active,
    hasRenderedCustomSection,
  ];

  return isAutoConfigsActive.some((isActive) => isActive);
};

const AdditionalInformation = () => {
  const { lob } = useLob();
  const { incidentConfiguration } = useIncidentConfiguration();
  const { setFieldValue, values } = useFormikContext();
  const { isFieldSupportedByCategories } = useIsConfigurationFieldSupportedByCategories();
  const { userOrganization } = useCms();
  const isHierarchyFieldGroupsEnabled = isFeatureEnabled(
    userOrganization,
    CONFIGURATION_FEATURES_NAMES.HIERARCHY_FIELD_GROUPS
  );

  const onSubmit = (sectionPath, formValues) => {
    setFieldValue(sectionPath, formValues);
  };

  const customSections = getCustomAdditionalInformationSections(incidentConfiguration);

  const GENERAL_SECTIONS_PROPS = [
    {
      id: 'supplementary_information',
      title: getSupplementaryInformationSectionTitle(incidentConfiguration),
      sectionValues: getIn(values, SUPPLEMENTARY_INFORMATION_PATH),
      onSubmit: (formValues) => onSubmit(SUPPLEMENTARY_INFORMATION_PATH, formValues),
      sectionConfig: getSupplementaryInformationSectionConfig(
        incidentConfiguration,
        isFieldSupportedByCategories,
        isHierarchyFieldGroupsEnabled
      ),
      AdditionalInformationFragment: SupplementaryInformationFragment,
      AdditionalInformationFragmentInitialValues: getSupplementaryInformationInitialValues(
        incidentConfiguration,
        lob,
        isFieldSupportedByCategories,
        isHierarchyFieldGroupsEnabled
      ),
      AdditionalInformationFragmentValidationSchema: getSupplementaryInformationValidationSchema(
        incidentConfiguration,
        lob,
        isFieldSupportedByCategories,
        isHierarchyFieldGroupsEnabled
      ),
    },
  ];

  const AUTO_SECTIONS_PROPS = [
    {
      id: AUTO_FNOL_SECTION_ROW_IDS.policeAndFireDepartment,
      title: 'Police and Fire Department Involvement',
      sectionValues: getIn(values, POLICE_AND_FIRE_DEPARTMENT_PATH),
      onSubmit: (formValues) => onSubmit(POLICE_AND_FIRE_DEPARTMENT_PATH, formValues),
      sectionConfig: getPoliceFireDepartmentInvolvementSectionConfig(
        incidentConfiguration,
        isFieldSupportedByCategories
      ),
      AdditionalInformationFragment: PoliceFireDepartmentInvolvementFragment,
      AdditionalInformationFragmentInitialValues: getPoliceFireDepartmentInvolvementInitialValues(
        incidentConfiguration,
        isFieldSupportedByCategories
      ),
      AdditionalInformationFragmentValidationSchema: getPoliceFireDepartmentInvolvementValidationSchema(
        incidentConfiguration,
        isFieldSupportedByCategories
      ),
    },
    {
      id: AUTO_FNOL_SECTION_ROW_IDS.initialFaultAssessment,
      title: 'Initial Fault Assessment',
      sectionValues: getIn(values, FAULT_ASSESSMENT_PATH),
      onSubmit: (formValues) => onSubmit(FAULT_ASSESSMENT_PATH, formValues),
      sectionConfig: getInitialFaultAssessmentSectionConfig(incidentConfiguration, isFieldSupportedByCategories),
      AdditionalInformationFragment: InitialFaultAssessmentFragment,
      AdditionalInformationFragmentInitialValues: getInitialFaultAssessmentInitialValues(
        incidentConfiguration,
        isFieldSupportedByCategories
      ),
      AdditionalInformationFragmentValidationSchema: getInitialFaultAssessmentInitialValidationSchema(
        incidentConfiguration,
        isFieldSupportedByCategories
      ),
    },
    {
      id: AUTO_FNOL_SECTION_ROW_IDS.initialTheftFollowUp,
      title: 'Initial Theft Follow-Up',
      sectionValues: getIn(values, THEFT_FOLLOW_UP_PATH),
      onSubmit: (formValues) => onSubmit(THEFT_FOLLOW_UP_PATH, formValues),
      sectionConfig: getInitialTheftFollowUpSectionConfig(incidentConfiguration, isFieldSupportedByCategories),
      AdditionalInformationFragment: InitialTheftFollowUpFragment,
      AdditionalInformationFragmentInitialValues: getInitialTheftFollowUpInitialValues(
        incidentConfiguration,
        isFieldSupportedByCategories
      ),
      AdditionalInformationFragmentValidationSchema: getInitialTheftFollowUpInitialValidationSchema(
        incidentConfiguration,
        isFieldSupportedByCategories
      ),
    },
    {
      id: AUTO_FNOL_SECTION_ROW_IDS.initialAccidentFollowUp,
      title: 'Initial Accident Follow-Up',
      sectionValues: getIn(values, ACCIDENT_FOLLOW_UP_PATH),
      onSubmit: (formValues) => onSubmit(ACCIDENT_FOLLOW_UP_PATH, formValues),
      sectionConfig: getInitialAccidentFollowUpSectionConfig(incidentConfiguration, isFieldSupportedByCategories),
      AdditionalInformationFragment: InitialAccidentFollowUpFragment,
      AdditionalInformationFragmentInitialValues: getInitialAccidentFollowUpInitialValues(
        incidentConfiguration,
        isFieldSupportedByCategories
      ),
      AdditionalInformationFragmentValidationSchema: getInitialAccidentFollowUpInitialValidationSchema(
        incidentConfiguration,
        isFieldSupportedByCategories
      ),
    },
  ];

  // AdditionalInformationSection gates non-supplementary rows on sectionConfig.active alone, so the
  // "has a rendered field" half of the rule is applied here (mirrors the id === 'supplementary_information'
  // short-circuit inside that component).
  const CUSTOM_SECTIONS_PROPS = customSections
    .filter((section) =>
      isCustomSectionRendered(
        incidentConfiguration,
        section,
        isFieldSupportedByCategories,
        isHierarchyFieldGroupsEnabled
      )
    )
    .map((section) => ({
      id: section.id,
      title: section.desc || section.id,
      sectionValues: getIn(values, getCustomSectionPath(section.id)),
      onSubmit: (formValues) => onSubmit(getCustomSectionPath(section.id), formValues),
      // Promote mandatory the way every sibling section does (getInitialSectionConfig), so a section with
      // a rendered mandatory field forces verification even when its own Required flag is off.
      sectionConfig: {
        ...section,
        mandatory: getIsCustomSectionMandatory(
          incidentConfiguration,
          section,
          isFieldSupportedByCategories,
          isHierarchyFieldGroupsEnabled
        ),
      },
      // The shared dialog forwards its `id` to the fragment as sectionId, so CustomSectionFragment is passed
      // directly instead of a per-section wrapper component (which would be a component inside a component).
      AdditionalInformationFragment: CustomSectionFragment,
      AdditionalInformationFragmentInitialValues: getCustomSectionInitialValues(
        incidentConfiguration,
        section,
        isFieldSupportedByCategories,
        isHierarchyFieldGroupsEnabled
      ),
      AdditionalInformationFragmentValidationSchema: getCustomSectionValidationSchema(
        incidentConfiguration,
        section,
        isFieldSupportedByCategories,
        isHierarchyFieldGroupsEnabled
      ),
    }));

  const getSectionsProps = (lob) => {
    switch (lob) {
      case 'auto_claim':
        return [...GENERAL_SECTIONS_PROPS, ...AUTO_SECTIONS_PROPS, ...CUSTOM_SECTIONS_PROPS];
      default:
        return [...GENERAL_SECTIONS_PROPS, ...CUSTOM_SECTIONS_PROPS];
    }
  };

  return (
    <CardDialog title="Additional Information" containerStyle={{ borderRadius: '6px' }}>
      <Grid container className={styles.AdditionalInformation}>
        <Grid item xs={12} className={styles.description}>
          <Typography display="block" variant="body1" color="textSecondary">
            {DESCRIPTION}
          </Typography>
        </Grid>
        {getSectionsProps(lob).map((sectionProps, index) => (
          <AdditionalInformationSection key={index} {...sectionProps} />
        ))}
      </Grid>
    </CardDialog>
  );
};

export { getIsAdditionalInformationActive, getSectionsValidationSchema };
export default AdditionalInformation;
